import React, { useCallback, useEffect } from 'react';
import { ColumnDataType, FavouriteDataColumn }
  from 'app/components/favourite-layout/favourite/common/favourite-interfaces';
import { DisplayDateValue, DisplayResultValue } from 'app/area/case-report/case-table-view/components/case-table-view-cells';
import { CASE_TABLE_VIEW_DATE_FORMAT } from 'app/area/case-report/case-table-view/components/case-table-view-util';
import styles from './../case-table-view.scss';
import { ComplexValue, Entity, StoreData } from 'app/types-shared';
import { CaseTableViewMeatballMenu } from 'app/area/case-report/case-table-view/components/case-table-view-meatball-menu';
import { CASE_NO_COLUMN_NAME } from 'app/area/case-report/case-table-view/constants';
import { TranslationType } from 'app/area/case-report/case-table-view/case-table-view-container';
import memoize from 'lodash/memoize';
import { AddRiskEvaluationInitialValues } from 'app/area/case/features/loss-potential-evaluation/types';
import { caseTableViewExternalUrlHook } from 'app/area/case-report/case-table-view/hooks/case-table-view-external-url-hook';

interface TableRowFieldsProps {
  row: Entity[];
  columns: Partial<FavouriteDataColumn>[];
  locale: string;
  index: number;
}

export const getCaseIdField = memoize((fields: Entity[]) => {
  const field = fields?.[0]?.[CASE_NO_COLUMN_NAME]?.[0] || null;
  if (field) {
    return field.value;
  }
  return null;
}, fields => JSON.stringify(fields));

const TableRowFields: React.FC<TableRowFieldsProps> = React.memo(({ row, columns, locale, index }) => {
  const fields = row?.[0];
  const renderCells = useCallback(() => {
    return columns.map((column, columnIndex) => {
      const itemTd = fields?.[column.columnName] as ComplexValue<number>[];
      if (!itemTd) {
        return <td key={`${index}-${columnIndex}`} className={styles.emptyColumn} />;
      }
      return (
        <td key={`${index}-${columnIndex}`} >
          {column.dataType === ColumnDataType.Date ?
            <DisplayDateValue
              componentId={`data-column-${index}-${columnIndex}`}
              cells={itemTd}
              dateTimeFormatter={CASE_TABLE_VIEW_DATE_FORMAT}
              column={column}
              locale={locale}
            />
            :
            <DisplayResultValue
              componentId={`data-column-${index}-${columnIndex}`}
              cells={itemTd}
              column={column}
            />
          }
        </td>
      );
    });
  }, [row, columns, locale]);

  return (
    <>
      {renderCells()}
    </>
  );
});

export const CaseTableViewRows = ({ rows, columns, locale, containerRef, translation, onDeleteCase, onCopyRiskEvaluation, tableViewRef }:
  {
    rows: StoreData[];
    columns: Partial<FavouriteDataColumn>[];
    locale: string;
    containerRef: React.RefObject<HTMLDivElement>;
    translation: TranslationType;
    onDeleteCase: (caseId: number) => void;
    onCopyRiskEvaluation: (caseId: number, riskMatrixInstance: number, instanceCopyCmd: number, 
      initialValues: AddRiskEvaluationInitialValues, copyToResidual: boolean) => void;
    tableViewRef?: React.RefObject<HTMLDivElement>;
  }) => {

    const { singleSupplierUrl, loadSingleSupplierUrl } = caseTableViewExternalUrlHook();
    useEffect(() => {
      loadSingleSupplierUrl();
    }, [loadSingleSupplierUrl]);

  if (!rows?.length) {
    return null;
  }

  return (
    <div className={styles.caseViewTable} ref={containerRef}>
      <table>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`row-${rowIndex}`}>
              <TableRowFields key={rowIndex} row={row} columns={columns} locale={locale} index={rowIndex} />
              <td className={styles.contextMenuWrapper}>
                <CaseTableViewMeatballMenu
                  caseId={getCaseIdField(row)}
                  translation={translation}
                  onDeleteCase={onDeleteCase}
                  onCopyRiskEvaluation={onCopyRiskEvaluation}
                  tableViewRef={tableViewRef}
                  singleSupplierUrl={singleSupplierUrl}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
